import { countryOptions, isCountryCode } from '@/modules/jobs/signal/countries';
import { isRemoteOnly } from '@/modules/jobs/signal/location';
import type { JobLocationType, RoleDraft } from '@/modules/jobs/signal/types';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { MultiSelect } from '@/modules/ui/MultiSelect';

interface JobCountriesFieldProps {
  countries: string[];
  isAnywhere: boolean;
  locationTypes: readonly JobLocationType[];
  onChange: (
    change: Partial<Pick<RoleDraft, 'countries' | 'isAnywhere'>>,
  ) => void;
  /** The form's help text style. */
  helpClassName: string;
}

/**
 * Where the job's search looks: its countries, or anywhere for a remote-only job. The search filters candidates to
 * these countries, so publishing needs one or the other. A region is several countries, picked one by one. Ticking
 * Anywhere keeps the picked countries in the draft, so unticking brings them back; the request leaves them out.
 */
export function JobCountriesField({
  countries,
  isAnywhere,
  locationTypes,
  onChange,
  helpClassName,
}: JobCountriesFieldProps) {
  const remoteOnly = isRemoteOnly(locationTypes);
  // Anywhere counts only while the job is remote-only; the request drops it otherwise.
  const anywhere = isAnywhere && remoteOnly;

  return (
    <>
      <MultiSelect
        aria-required
        options={countryOptions}
        defaultValue={anywhere ? [] : countries}
        onValueChange={(v) => onChange({ countries: v.filter(isCountryCode) })}
        placeholder={anywhere ? 'Anywhere' : 'Search countries'}
        disabled={anywhere}
        hideSelectAll
        maxCount={6}
        className="h-auto min-h-9"
      />
      {remoteOnly && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={anywhere}
            onCheckedChange={(checked) =>
              onChange({ isAnywhere: checked === true })
            }
          />
          Anywhere: a hire may work from any country
        </label>
      )}
      <p className={helpClassName}>
        Finlay searches only these countries. For a region, pick each of its
        countries.
      </p>
    </>
  );
}
